import {
  ACESFilmicToneMapping,
  Color,
  DirectionalLight,
  Euler,
  HemisphereLight,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PCFShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  PMREMGenerator,
  Scene,
  ShadowMaterial,
  SphereGeometry,
  SRGBColorSpace,
  Vector2,
  Vector3,
  WebGLRenderer,
} from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";
import {
  defaultCameraPreset,
  findCameraPreset,
  type ReviewHotspot,
  type ReviewScene,
} from "../shared/reviewScene";
import {
  cadPointToScene,
  ReviewSceneRegistry,
  type SceneVisibility,
} from "./sceneRegistry";

export interface PlannerSceneContext {
  scene: Scene;
  camera: PerspectiveCamera;
  renderer: WebGLRenderer;
  domElement: HTMLCanvasElement;
  coordinateRoot: ReviewSceneRegistry["coordinateRoot"];
}

export class ReviewViewer {
  readonly scene = new Scene();
  readonly camera = new PerspectiveCamera(42, 1, 0.015, 100);
  readonly renderer: WebGLRenderer;
  readonly registry: ReviewSceneRegistry;

  #canvas: HTMLCanvasElement;
  #viewport: HTMLElement;
  #controls: OrbitControls;
  #manifest: ReviewScene | undefined;
  #visibility: SceneVisibility = {
    shell: true,
    coreInterior: true,
    linings: true,
    insulation: true,
  };
  #hiddenNodes: readonly string[] = [];
  #activeView = "cutaway";
  #walking = false;
  #dragging = false;
  #lastPointer = new Vector2();
  #resizeObserver: ResizeObserver;
  #ground: Mesh<PlaneGeometry, ShadowMaterial>;
  #hotspotMarker: Mesh<SphereGeometry, MeshStandardMaterial>;
  #environmentTexture: ReturnType<PMREMGenerator["fromScene"]>["texture"];
  #leftInset: number | (() => number) = 0;
  #lastRenderAt = 0;
  readonly #minimumRenderIntervalMs = navigator.webdriver ? 100 : 0;

  constructor(canvas: HTMLCanvasElement, viewport: HTMLElement) {
    this.#canvas = canvas;
    this.#viewport = viewport;
    this.scene.background = new Color(0xe7f1ee);

    this.renderer = new WebGLRenderer({ canvas, antialias: true, powerPreference: "high-performance" });
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.04;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFShadowMap;
    this.renderer.setPixelRatio(navigator.webdriver ? 1 : Math.min(devicePixelRatio, 2));

    const pmrem = new PMREMGenerator(this.renderer);
    const room = new RoomEnvironment();
    this.#environmentTexture = pmrem.fromScene(room, 0.04).texture;
    this.scene.environment = this.#environmentTexture;
    room.dispose();
    pmrem.dispose();

    this.scene.add(new HemisphereLight(0xf8fcff, 0x5c6c65, 1.55));
    const sun = new DirectionalLight(0xffffff, 2.65);
    sun.position.set(5, 8, 5);
    sun.castShadow = true;
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.camera.left = -7;
    sun.shadow.camera.right = 7;
    sun.shadow.camera.top = 7;
    sun.shadow.camera.bottom = -7;
    this.scene.add(sun);

    this.camera.up.set(0, 1, 0);
    this.#controls = new OrbitControls(this.camera, canvas);
    this.#controls.enableDamping = true;
    this.#controls.dampingFactor = 0.075;
    this.#controls.screenSpacePanning = true;
    this.#controls.minDistance = 0.15;
    this.#controls.maxDistance = 30;

    this.registry = new ReviewSceneRegistry(this.scene);
    this.#ground = new Mesh(
      new PlaneGeometry(30, 30),
      new ShadowMaterial({ color: 0x38554b, opacity: 0.13 }),
    );
    this.#ground.rotation.x = -Math.PI / 2;
    this.#ground.receiveShadow = true;
    this.scene.add(this.#ground);

    this.#hotspotMarker = new Mesh(
      new SphereGeometry(0.035, 20, 12),
      new MeshStandardMaterial({ color: 0xe5a12b, emissive: 0x683800, emissiveIntensity: 0.45 }),
    );
    this.#hotspotMarker.name = "active_review_hotspot";
    this.#hotspotMarker.visible = false;
    this.#hotspotMarker.renderOrder = 20;
    this.scene.add(this.#hotspotMarker);

    this.#wireWalkLook();
    this.#resizeObserver = new ResizeObserver(() => this.#resize());
    this.#resizeObserver.observe(viewport);
    this.#resize();
    this.renderer.setAnimationLoop(() => this.#render());
  }

  get plannerContext(): PlannerSceneContext {
    return {
      scene: this.scene,
      camera: this.camera,
      renderer: this.renderer,
      domElement: this.#canvas,
      coordinateRoot: this.registry.coordinateRoot,
    };
  }

  get activeView(): string {
    return this.#activeView;
  }

  get walking(): boolean {
    return this.#walking;
  }

  async load(manifest: ReviewScene, manifestUrl: URL): Promise<void> {
    this.#manifest = manifest;
    await this.registry.load(manifest, manifestUrl);
    this.#ground.position.y = this.registry.bounds.min.y - 0.002;
    const firstCamera = defaultCameraPreset(manifest);
    this.setView(firstCamera.id);

    // A tiny enclosed basic mesh keeps software WebGL renderers deterministic.
    const primer = new Mesh(
      new SphereGeometry(0.01, 6, 4),
      new MeshBasicMaterial({ color: 0x777777 }),
    );
    primer.name = "render_primer_inside_slab";
    primer.position.copy(this.registry.center);
    primer.position.y = this.registry.bounds.min.y + 0.025;
    this.scene.add(primer);
  }

  setView(id: string): void {
    if (!this.#manifest) return;
    const preset = findCameraPreset(this.#manifest, id);
    this.setWalking(false);
    this.registry.applyCamera(preset, this.camera.position, this.#controls.target, this.camera.up);
    this.camera.lookAt(this.#controls.target);
    this.camera.updateProjectionMatrix();
    this.#controls.update();
    this.#activeView = id;
    this.#hiddenNodes = preset.hidden_nodes;
    this.registry.applyVisibility(this.#visibility, this.#hiddenNodes);
    this.clearHotspot();
  }

  setVisibility(next: Partial<SceneVisibility>): void {
    this.#visibility = { ...this.#visibility, ...next };
    if (this.#manifest) this.registry.applyVisibility(this.#visibility, this.#hiddenNodes);
  }

  setOrbitInteractionEnabled(enabled: boolean): void {
    this.#controls.enabled = enabled && !this.#walking;
  }

  setViewportLeftInset(inset: number | (() => number)): void {
    this.#leftInset = inset;
    this.#resize();
  }

  setWalking(active: boolean): void {
    if (active === this.#walking) return;
    if (active && this.#manifest) {
      const inside = this.#manifest.camera_presets.find((preset) => preset.id === "inside");
      if (inside) {
        this.registry.applyCamera(inside, this.camera.position, this.#controls.target, this.camera.up);
        this.camera.lookAt(this.#controls.target);
        this.#activeView = "inside";
        this.#hiddenNodes = inside.hidden_nodes;
        this.registry.applyVisibility(this.#visibility, this.#hiddenNodes);
      }
    }
    this.#walking = active;
    this.#controls.enabled = !active;
    this.#dragging = false;
    if (!active) {
      const forward = new Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
      this.#controls.target.copy(this.camera.position).addScaledVector(forward, 1.2);
      this.#controls.update();
    }
  }

  focusHotspot(hotspot: ReviewHotspot): void {
    this.setView(hotspot.camera_preset_id);
    cadPointToScene(hotspot.position_mm, this.#controls.target);
    this.camera.lookAt(this.#controls.target);
    this.#controls.update();
    this.#hotspotMarker.position.copy(this.#controls.target);
    this.#hotspotMarker.visible = true;
  }

  clearHotspot(): void {
    this.#hotspotMarker.visible = false;
  }

  dispose(): void {
    this.renderer.setAnimationLoop(null);
    this.#resizeObserver.disconnect();
    this.#controls.dispose();
    this.registry.dispose();
    this.#ground.geometry.dispose();
    this.#ground.material.dispose();
    this.#hotspotMarker.geometry.dispose();
    this.#hotspotMarker.material.dispose();
    this.#environmentTexture.dispose();
    this.renderer.dispose();
  }

  #resize(): void {
    const width = Math.max(1, this.#viewport.clientWidth);
    const height = Math.max(1, this.#viewport.clientHeight);
    const requestedInset = typeof this.#leftInset === "function" ? this.#leftInset() : this.#leftInset;
    const inset = Math.max(0, Math.min(Math.round(requestedInset), width - 1));
    const renderWidth = Math.max(1, width - inset);
    this.#canvas.style.left = `${inset}px`;
    this.#canvas.style.width = `${renderWidth}px`;
    this.#canvas.style.height = `${height}px`;
    this.camera.aspect = renderWidth / height;
    this.camera.zoom = inset > 0 ? Math.max(0.5, Math.min(1, renderWidth / (height * 0.8))) : 1;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(renderWidth, height, false);
  }

  #render(): void {
    const now = performance.now();
    if (now - this.#lastRenderAt < this.#minimumRenderIntervalMs) return;
    this.#lastRenderAt = now;
    if (!this.#walking) this.#controls.update();
    if (this.#hotspotMarker.visible) {
      const pulse = 1 + Math.sin(now * 0.006) * 0.16;
      this.#hotspotMarker.scale.setScalar(pulse);
    }
    this.renderer.render(this.scene, this.camera);
  }

  #wireWalkLook(): void {
    this.#canvas.addEventListener("pointerdown", (event) => {
      if (!this.#walking || event.button !== 0) return;
      this.#dragging = true;
      this.#lastPointer.set(event.clientX, event.clientY);
      this.#canvas.setPointerCapture(event.pointerId);
    });
    this.#canvas.addEventListener("pointermove", (event) => {
      if (!this.#walking || !this.#dragging) return;
      const deltaX = event.clientX - this.#lastPointer.x;
      const deltaY = event.clientY - this.#lastPointer.y;
      this.#lastPointer.set(event.clientX, event.clientY);

      const euler = new Euler().setFromQuaternion(this.camera.quaternion, "YXZ");
      euler.y -= deltaX * 0.004;
      euler.x = Math.max(-Math.PI * 0.48, Math.min(Math.PI * 0.48, euler.x - deltaY * 0.004));
      euler.z = 0;
      this.camera.quaternion.setFromEuler(euler);
    });
    const stopDrag = (event: PointerEvent): void => {
      this.#dragging = false;
      if (this.#canvas.hasPointerCapture(event.pointerId)) this.#canvas.releasePointerCapture(event.pointerId);
    };
    this.#canvas.addEventListener("pointerup", stopDrag);
    this.#canvas.addEventListener("pointercancel", stopDrag);
  }
}
